import type { LucideIcon } from 'lucide-react'
import { ArrowUp, ArrowDown } from 'lucide-react'
import { cn } from '@/lib/utils'

/** The five tint options from the reference design's stat row. */
export type StatTint = 'purple' | 'green' | 'orange' | 'red' | 'blue'

const TINTS: Record<StatTint, { surface: string; icon: string; accent: string }> = {
  purple: { surface: 'bg-[var(--color-tint-purple)]', icon: 'bg-[var(--color-primary)]/12 text-[var(--color-primary)]', accent: 'text-[var(--color-primary)]' },
  green: { surface: 'bg-[var(--color-tint-green)]', icon: 'bg-[var(--color-success)]/12 text-[var(--color-success)]', accent: 'text-[var(--color-success)]' },
  orange: { surface: 'bg-[var(--color-tint-orange)]', icon: 'bg-[var(--color-warning)]/12 text-[var(--color-warning)]', accent: 'text-[var(--color-warning)]' },
  red: { surface: 'bg-[var(--color-tint-red)]', icon: 'bg-[var(--color-danger)]/12 text-[var(--color-danger)]', accent: 'text-[var(--color-danger)]' },
  blue: { surface: 'bg-[var(--color-tint-blue)]', icon: 'bg-[var(--color-info)]/12 text-[var(--color-info)]', accent: 'text-[var(--color-info)]' },
}

export interface StatCardProps {
  label: string
  value: string | number
  /** Secondary line under the value, e.g. "84.0% of total". */
  caption?: string
  /** Change indicator. Positive renders an up arrow, negative a down arrow. */
  delta?: { value: string; direction: 'up' | 'down' | 'flat' }
  icon: LucideIcon
  tint?: StatTint
  className?: string
}

/**
 * One of the five headline metrics across the top of the dashboard.
 *
 * The delta arrow is paired with its sign in the text rather than relying on colour
 * alone, so the trend is still readable to colour-blind users (WCAG 1.4.1).
 */
export function StatCard({
  label,
  value,
  caption,
  delta,
  icon: Icon,
  tint = 'purple',
  className,
}: StatCardProps) {
  const theme = TINTS[tint]
  const deltaColor =
    delta?.direction === 'down' ? 'text-[var(--color-danger)]' : theme.accent

  return (
    <div
      className={cn(
        'rounded-[var(--radius-card)] border border-[var(--color-border)] p-4',
        theme.surface,
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-[var(--color-body)]">{label}</p>
          <p className="mt-1.5 text-2xl font-semibold tabular-nums text-[var(--color-heading)]">
            {value}
          </p>
        </div>
        <span className={cn('grid size-10 shrink-0 place-items-center rounded-full', theme.icon)}>
          <Icon className="size-5" aria-hidden="true" />
        </span>
      </div>

      {(delta || caption) && (
        <p className={cn('mt-2 flex items-center gap-1 text-xs font-medium', deltaColor)}>
          {delta?.direction === 'up' && <ArrowUp className="size-3" aria-hidden="true" />}
          {delta?.direction === 'down' && <ArrowDown className="size-3" aria-hidden="true" />}
          <span>{delta?.value ?? caption}</span>
        </p>
      )}
    </div>
  )
}
